import router from "../router";
import Vuetify from "../plugins/vuetify";
import { getField, updateField } from "vuex-map-fields";

import {
	ALERT_HIDDEN
} from "@/common/const";
import useVoice from "@/composables/voice";

export default {
	namespaced: true,
	state: () => ({
		ready: false,
		enabled: false,
		incomingCall: false,
		isOnCall: false,
		isMicroGuideOpen: false,
		isCallDialogAttached: false,
		activeCall: null,
		callDetails: null,
		isCallAccepted: false,
		loadingCallDetails: false,
		isMicroOn: true,
		localStream: null,
		headsetDevice: null,
		headsetDeviceReportId: null,
		activeCallConvoSid: undefined,
		activeCallConvoState: undefined,
		draggableIsMoving: false,
		showIncomingCallAlert: ALERT_HIDDEN,
		// Audio device management
		audioInputs: [],
		audioOutputs: [],
		activeInputId: null,
		activeOutputId: null
	}),

	getters: {
		getField,
		isReady: (state) => state.ready,
		hasIncomingCall: (state) => state.incomingCall,
		isOnCall: (state) => state.isOnCall,
		hasOutboundCall: (state) => state.activeCall && !state.incomingCall,
		callDetails : (state) => state.callDetails,
		isMicroGuideOpen: (state) => state.isMicroGuideOpen,
		isCallDialogAttached: (state) => {
			const isConversations = router.app.$route.path == "/conversations";

			if (isConversations && (state.isOnCall || state.incomingCall || Vuetify.framework.breakpoint.smAndDown)) return state.isCallDialogAttached;
			return state.isCallDialogAttached = isConversations;
		},
		activeCall: (state) => state.activeCall,
		isCallAccepted: (state) => state.isCallAccepted,
		isMicroOn: (state) => state.isMicroOn,
		localStream: (state) => state.localStream,
		headsetDevice: (state) => state.headsetDevice,
		headsetDeviceReportId: (state) => state.headsetDeviceReportId,
		activeCallConvoSid: (state) => state.activeCallConvoSid,
		activeCallConvoState: (state) => state.activeCallConvoState,
		draggableIsMoving: (state) => state.draggableIsMoving,
		loadingCallDetails: (state) => state.loadingCallDetails,
		audioInputs: (state) => state.audioInputs,
		audioOutputs: (state) => state.audioOutputs,
		activeInputId: (state) => state.activeInputId,
		activeOutputId: (state) => state.activeOutputId
	},

	mutations: {
		updateField,
		setReady: (state, isReady) => state.ready = isReady,
		setInboundCall: (state, callInstance) => {
			if (!state.incomingCall && !state.isOnCall) {
				state.activeCall = callInstance;
				state.incomingCall = true;
				state.isOnCall = false;
				state.isCallAccepted = false;
			}
		},
		setInboundQueueCall: (state, callInstance) => {
			state.activeCall = callInstance;
			state.incomingCall = false;
			state.isOnCall = true;
			state.isCallAccepted = true;
		},
		setOutboundCall: (state, callInstance) => {
			state.activeCall = callInstance;
			state.incomingCall = false;
			state.isOnCall = true;
			state.isCallAccepted = false;
		},
		setCallDetails: (state, callDetails) => {
			state.callDetails = callDetails;
		},
		setLoadingCallDetails: (state, loadingCallDetails) => {
			state.loadingCallDetails = loadingCallDetails;
		},
		setIsMicroGuideOpen: (state, isMicroGuideOpen) => state.isMicroGuideOpen = isMicroGuideOpen,
		setCallDialogAttached: (state, isCallDialogAttached) => state.isCallDialogAttached = isCallDialogAttached,
		setIsOnCall: (state, isOnCall) => state.isOnCall = isOnCall,
		setIsCallAccepted: (state, isCallAccepted) => {
			state.isCallAccepted = isCallAccepted;
		},
		setIsMicroOn: (state, isMicroOn) => state.isMicroOn = isMicroOn,
		enableMicrophone: (state) => {
			state.activeCall?.mute(false);

			// Build audio constraints targeting the preferred/active input device
			const audioConstraints = {};
			const deviceId = state.activeInputId;

			if (deviceId && deviceId !== "default") {
				audioConstraints.deviceId = { exact: deviceId };
			}

			navigator.mediaDevices
				.getUserMedia({ audio: Object.keys(audioConstraints).length ? audioConstraints : true })
				.then((stream) => {
					state.localStream = stream;
				})
				.catch((err) => {
					console.warn("[Voice] Failed to getUserMedia with preferred device, falling back to default:", err.message);
					// Fallback to default device
					navigator.mediaDevices
						.getUserMedia({ audio: true })
						.then((stream) => {
							state.localStream = stream;
						})
						.catch((fallbackErr) => {
							console.error("[Voice] Failed to get any audio input:", fallbackErr);
						});
				});
		},

		setHeadsetDevice: (state, device) => {
			state.headsetDevice = device;
		},

		setHeadsetDeviceReportId: (state, reportId) => {
			state.headsetDeviceReportId = reportId;
		},

		disableMicrophone: (state) => {
			state.activeCall?.mute(true);
			state.localStream?.getTracks().forEach((track) => {
				track.stop();
			});
		},

		setAudioDevices: (state, { audioInputs, audioOutputs, activeInputId, activeOutputId }) => {
			state.audioInputs = audioInputs ?? state.audioInputs;
			state.audioOutputs = audioOutputs ?? state.audioOutputs;
			state.activeInputId = activeInputId ?? state.activeInputId;
			state.activeOutputId = activeOutputId ?? state.activeOutputId;
		},

		setDraggableIsMoving: (state, draggableIsMoving) => state.draggableIsMoving = draggableIsMoving,

		reset: (state) => {
			if (state.activeCall) {
				if (state.incomingCall) {
					state.activeCall.reject();
				} else {
					state.activeCall.hangUp();
				}
			}

			state.ready = false;
			state.enabled = false;
			state.incomingCall = false;
			state.isOnCall = false;
			state.isMicroGuideOpen = false;
			state.isCallDialogAttached = false;
			state.draggableIsMoving = false;
			state.isCallAccepted = false;
			state.isMicroOn = (state.isMicroOn !== false);

			state.activeCall= null;
			state.callDetails = null;
			state.localStream = null;
			state.activeCallConvoSid = undefined;
			state.activeCallConvoState = undefined;
		},

		acceptCall: (state) => {
			if (state.activeCall) {
				state.incomingCall = false;
				state.isCallAccepted = true;
				state.isOnCall = true;
			}
		},

		disconnectCall: (state) => {
			if (state.incomingCall || state.isOnCall) {
				state.incomingCall = false;
				state.isOnCall = false;
				state.isCallAccepted = false;
				state.activeCall = null;
				state.callDetails = null;
			}
		}
	},

	actions: {
		setMicrophoneDialog({ commit }, newValue) {
			commit("SET_MICROPHONE_DIALOG", newValue);
		},

		async endCall({ state, commit }) {
			if (state.callDetails?.id) {
				await commit("calls/releaseCall", state.callDetails.id, { root: true });
			}

			commit("disconnectCall");
		}
	},

	init({ watch, login, commit }, rootStore) {
		watch(
			() => login.profile?.id,
			(newId, oldId) => {
				if (newId === oldId) return;

				console.log("[CALL] user profile changed:", oldId, "->", newId);

				if (oldId) {
					useVoice.cleanup(commit);
				}

				if (newId) {
					useVoice.bootstrap(commit);
				}
			}
		);

		watch(
			() => login.refreshed,
			(refreshed) => {
				if (refreshed) {
					useVoice.bootstrap(commit);
				}
			}
		);

		watch(
			() => rootStore.getters["clinic/clinicData"]?.options?.twilio?.webCallsEnabled,
			(newEnabled, oldEnabled) => {
				if (newEnabled === oldEnabled || oldEnabled === undefined || newEnabled === undefined) return;

				console.log("[CALL] webCallsEnabled changed:", oldEnabled, "->", newEnabled);

				if (oldEnabled) {
					useVoice.cleanup(commit);
				}

				if (newEnabled) {
					useVoice.bootstrap(commit);
				}
			}
		);
	}
};
